import React from "react";
import { Box, Typography } from "@mui/material";
import MarkdownRenderer from "../MarkdownRenderer/MarkdownRenderer";

// Match "Query refined: <text>" or "Refined query: <text>" (rest of line); use last occurrence for follow-ups
const REFINED_QUERY_PATTERN = /(?:Query refined|Refined query):\s*(.+?)(?=\n|$)/gi;

/**
 * For feedback, prefer the refined question from assistant content (follow-up) over the raw user message.
 * @param {string} messageContent - Current assistant message content
 * @param {string} fallbackQuestion - Last user message (used when no refined query is found)
 * @returns {string} Refined query text if found, otherwise fallback
 */
const getQuestionForFeedback = (messageContent, fallbackQuestion) => {
  if (!messageContent || typeof messageContent !== "string") return fallbackQuestion || "";
  let match;
  let lastRefined = null;
  REFINED_QUERY_PATTERN.lastIndex = 0;
  while ((match = REFINED_QUERY_PATTERN.exec(messageContent)) !== null) {
    lastRefined = match[1].trim();
  }
  return (lastRefined && lastRefined.length > 0) ? lastRefined : (fallbackQuestion || "");
};

export const MessageList = ({
  messages,
  currentStreamingMessageId,
  onQuestionClick,
  onRetry,
  modelOptions = [],
  selectedModel = "",
  isLoading,
  onUpdateMessageFeedback,
  dispatchMessages,
}) => {
  // Index of the last assistant message (retry icon only shown for this one)
  const lastAssistantIndex = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") return i;
    }
    return -1;
  })();

  // For an assistant message, get the question that led to it (last user message before this one)
  const getQuestionForMessage = (messages, currentIndex) => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (messages[i].role === "user" && messages[i].content) {
        return typeof messages[i].content === "string" ? messages[i].content : "";
      }
    }
    return "";
  };

  return (
    <>
      {messages.map((message, index) => {
        const isAssistant = message.role === "assistant";
        const isLive = message.id === currentStreamingMessageId;
        // Show thinking dots for the current streaming message until streaming completes.
        const showDots = isLive;
        const lastUserMessage = isAssistant ? getQuestionForMessage(messages, index) : "";
        // For follow-ups: use "Query refined: ..." from assistant content; otherwise use last user message (base query)
        const question = isAssistant
          ? getQuestionForFeedback(message.content || "", lastUserMessage)
          : "";

        return (
          <Box
            key={message.id}
            sx={{
              display: "flex",
              justifyContent: isAssistant ? "flex-start" : "flex-end",
              animation: "fadeIn 0.3s ease-out",
              "@keyframes fadeIn": {
                from: { opacity: 0, transform: "translateY(8px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                maxWidth: { xs: "95%", sm: "92%", md: "90%" },
                width: "fit-content",
              }}
            >
              {isAssistant ? (
                <Box
                  sx={{
                    backgroundColor: "#f8fafc",
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <MarkdownRenderer
                    content={message.content || (isLive ? "Thinking..." : "")}
                    question={question}
                    nlp_message_id={message.nlp_message_id ?? null}
                    messageFeedback={message.feedback ?? null}
                    messageModelId={message.model_id ?? null}
                    isLastAssistantMessage={isAssistant && index === lastAssistantIndex}
                    onRetry={
                      onRetry && question
                        ? (modelId) => onRetry(question, message.id, message.nlp_message_id, modelId)
                        : undefined
                    }
                    modelOptions={modelOptions}
                    selectedModel={selectedModel}
                    isLoading={isLoading}
                    onFeedbackSuccess={
                      onUpdateMessageFeedback && dispatchMessages
                        ? (nlp_message_id, feedback) => {
                            onUpdateMessageFeedback({ nlp_message_id, feedback });
                            dispatchMessages({ type: "SET_MESSAGE_FEEDBACK", nlp_message_id, feedback });
                          }
                        : undefined
                    }
                  />
                  {showDots && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        mt: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.375,
                          alignItems: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            backgroundColor: "#3F49A7",
                            animation: "thinking 1.4s ease-in-out infinite",
                            "@keyframes thinking": {
                              "0%, 60%, 100%": {
                                transform: "translateY(0)",
                                opacity: 0.6,
                              },
                              "30%": {
                                transform: "translateY(-10px)",
                                opacity: 1,
                              },
                            },
                          }}
                        />
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            backgroundColor: "#3F49A7",
                            animation: "thinking 1.4s ease-in-out infinite",
                            animationDelay: "0.2s",
                          }}
                        />
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            backgroundColor: "#3F49A7",
                            animation: "thinking 1.4s ease-in-out infinite",
                            animationDelay: "0.4s",
                          }}
                        />
                      </Box>
                      {/* <Typography
                        variant="caption"
                        sx={{
                          color: "#475569",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        Thinking...
                      </Typography> */}
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    backgroundColor: "#f5f3ff",
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                    maxWidth: "100%",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.5,
                      fontSize: "0.8125rem",
                      fontWeight: 500,
                      color: "#334155",
                    }}
                  >
                    {message.content}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </>
  );
};
